import { SingleDatePickerItem } from '../interfaces/single-date-picker-item.interface';

/**
 * Check if a given Date object and date item represent the same day.
 * @param date Date object to use in comparison
 * @param dateItem SingleDatePickerItem to use in comparison
 * @returns boolean that is true if the given date and date item represent the same day
 */
export const dateEqualsDateItem = function (date: Date, dateItem: SingleDatePickerItem): boolean {
    return (
        dateItem.year === date.getFullYear() && dateItem.month === date.getMonth() && dateItem.day === date.getDate()
    );
};
/**
 * Check if a given date item is inside a date range defined by two Date objects
 * @param dateItem SingleDatePickerItem to compare against the date range
 * @param startDate start of the date range to compare against
 * @param endDate end of the date range to compare against
 * @returns boolean whether or not the date item is within the date range
 */
export const isDateItemWithinDateRange = function (
    dateItem: SingleDatePickerItem,
    startDate: Date | null,
    endDate: Date | null
): boolean {
    if (startDate instanceof Date && endDate instanceof Date) {
        const dateItemDate: Date = new Date(dateItem.year, dateItem.month, dateItem.day);
        return startDate <= dateItemDate && dateItemDate <= endDate;
    }
    return false;
};

/**
 * Checks whether a date makes sense to be selected as part of a date range based on the role
 * of the date picker. For example, If a date picker is acting as the start date for a date range,
 * then no dates after the end date should be selectable in the date picker.
 * @param dateItemOrDate SingleDatePickerItem or Date to check for validity
 * @param startDate start of date range to check
 * @param endDate end of date range to check
 * @returns boolean whether or not the date item should be selectable
 */
export const isValidDateForDateRange = function (
    dateItemOrDate: SingleDatePickerItem | Date,
    selectedStartDate: Date | null,
    selectedEndDate: Date | null,
    isStartOfRange: boolean,
    startDate?: Date,
    endDate?: Date
): boolean {
    let isValid = true;
    const date: SingleDatePickerItem | Date =
        dateItemOrDate instanceof Date
            ? dateItemOrDate
            : new Date(dateItemOrDate.year, dateItemOrDate.month, dateItemOrDate.day);
    const dateGreaterThanEndDateLimit = endDate && date > endDate;
    const dateLessThanStartDateLimit = startDate && date < startDate;
    if (dateLessThanStartDateLimit || dateGreaterThanEndDateLimit) {
        return false;
    }
    if (isStartOfRange) {
        if (selectedEndDate) {
            // The selected start date cannot be after the selected end date
            isValid = date <= selectedEndDate;
        }
    } else if (selectedStartDate) {
        // The selected end date cannot be before the selected start date
        isValid = date >= selectedStartDate;
    }
    return isValid;
};
